/**
 * GameEventHandler - Handles game events like unit death, building placement, etc.
 */

import { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import { EventBus } from '@/core/events/EventBus';
import { useGameStore } from '@/state/gameStore';
import { audioManager } from '@/core/audio/AudioManager';
import { FACTIONS } from '@/game/factions/FactionData';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import { wallConnectionSystem } from '@/game/systems/WallConnectionSystem';
import type { FactionId } from '@/types';
import type { PositionComponent } from '@/game/components/Position';
import type { WallComponent } from '@/game/components/Wall';
import type { EventLog } from '@/ui/components/EventLog';
import type { Minimap } from '@/ui/components/Minimap';
import type { Camera } from '@/rendering/Camera';
import type { CampfireEffect } from '@/rendering/effects/CampfireEffect';
import type { FlagAnimation } from '@/rendering/effects/FlagAnimation';
import type { BuildingManager } from './BuildingManager';

export class GameEventHandler {
  private world: World;
  private eventLog: EventLog;
  private minimap: Minimap;
  private camera: Camera;
  private campfireEffect: CampfireEffect;
  private flagAnimation: FlagAnimation;
  private buildingManager: BuildingManager;

  constructor(
    world: World,
    eventLog: EventLog,
    minimap: Minimap,
    camera: Camera,
    campfireEffect: CampfireEffect,
    flagAnimation: FlagAnimation,
    buildingManager: BuildingManager
  ) {
    this.world = world;
    this.eventLog = eventLog;
    this.minimap = minimap;
    this.camera = camera;
    this.campfireEffect = campfireEffect;
    this.flagAnimation = flagAnimation;
    this.buildingManager = buildingManager;

    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    EventBus.on('unit:death', (data) => {
      if (data.factionId === useGameStore.getState().playerFaction) {
        this.eventLog.addEvent('单位阵亡', '我方单位阵亡', 'battle');
        audioManager.playSFX('unit_death');
      }
    });

    EventBus.on('territory:captured', (data) => {
      if (data.newOwner === useGameStore.getState().playerFaction && data.oldOwner && data.oldOwner !== 'neutral') {
        this.eventLog.notifyTerritory(FACTIONS[data.oldOwner as FactionId]?.name || '敌方');
        audioManager.playSFX('notification');
      }
    });

    EventBus.on('building:placed', (data) => {
      if (data.factionId === useGameStore.getState().playerFaction) {
        this.eventLog.notifyBuildingComplete(data.buildingType);
        audioManager.playSFX('build_complete');
      }

      const campfireBuildings = ['smithy', 'barracks', 'stable'];
      if (campfireBuildings.includes(data.buildingType)) {
        const campfireId = `campfire_${data.entityId || Date.now()}`;
        const intensity = data.buildingType === 'smithy' ? 1.0 : 0.6;
        const offsetX = data.buildingType === 'smithy' ? 0.5 : 0.8;
        const offsetZ = data.buildingType === 'smithy' ? 0.5 : -0.8;
        this.campfireEffect.addCampfire(campfireId, data.x + offsetX, 0.1, data.z + offsetZ, intensity);
      }

      const flagBuildings = ['capital', 'barracks', 'tower'];
      if (flagBuildings.includes(data.buildingType)) {
        const flagId = `flag_${data.entityId || Date.now()}`;
        const flagSize = data.buildingType === 'capital' ? 'large' : data.buildingType === 'barracks' ? 'medium' : 'small';
        const flagY = data.buildingType === 'capital' ? 4.5 : data.buildingType === 'barracks' ? 2.0 : 2.8;
        this.flagAnimation.addFlag(flagId, data.x, flagY, data.z, data.factionId as FactionId, flagSize);
      }
    });

    EventBus.on('building:upgrade', (data) => {
      this.buildingManager.upgradeBuilding(data.entityId);
    });

    EventBus.on('building:repair', (data) => {
      this.buildingManager.repairBuilding(data.entityId);
    });

    EventBus.on('building:destroyed', (data) => {
      const wallComponent = this.world.getComponent<WallComponent>(data.entityId, ComponentTypes.Wall);
      if (wallComponent) {
        wallConnectionSystem.unregisterWall(wallComponent.tileX, wallComponent.tileZ);
        EventBus.emit('wall:destroyed', {
          entityId: data.entityId,
          tileX: wallComponent.tileX,
          tileZ: wallComponent.tileZ,
        });
      }

      // Remove flag if building had one
      const flagId = `flag_${data.entityId}`;
      this.flagAnimation.removeFlag(flagId);

      tileOccupancy.releaseTiles(data.entityId);

      if (data.factionId === useGameStore.getState().playerFaction) {
        this.eventLog.addEvent('建筑被毁', '我方建筑被摧毁', 'battle');
        audioManager.playSFX('unit_death');
      }
    });

    EventBus.on('unit:spawn', (data) => {
      if (data.factionId === useGameStore.getState().playerFaction) {
        this.eventLog.notifyUnitTrained(data.unitType);
        audioManager.playSFX('unit_trained');
      }
    });

    EventBus.on('unit:attack', (data) => {
      const targetPos = this.world.getComponent<PositionComponent>(data.targetId, ComponentTypes.Position);
      if (targetPos) {
        this.minimap.addBattleMarker(targetPos.x, targetPos.z);
        audioManager.playSFX3D('sword_hit', targetPos.x, targetPos.z, this.camera.target.x, this.camera.target.z);
      }
    });

    EventBus.on('diplomacy:war', (data) => {
      const attacker = FACTIONS[data.attacker as FactionId];
      const target = FACTIONS[data.target as FactionId];
      this.eventLog.addEvent('宣战', `${attacker.name} 向 ${target.name} 宣战!`, 'battle');
      audioManager.playSFX('notification');
    });

    EventBus.on('diplomacy:treaty', (data) => {
      const [a, b] = data.parties;
      const factionA = FACTIONS[a as FactionId];
      const factionB = FACTIONS[b as FactionId];
      this.eventLog.addEvent('条约签订', `${factionA.name} 与 ${factionB.name} 签订了条约`, 'info');
      audioManager.playSFX('notification');
    });

    EventBus.on('tech:completed', (data) => {
      if (data.factionId === useGameStore.getState().playerFaction) {
        this.eventLog.addEvent('研究完成', `${data.techName} 已研究完成`, 'info');
      }
    });
  }
}
